import { useState, useEffect } from "react";
import { Switch, Route, Link, useLocation } from "wouter";
import { Phone, Mail, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Toaster } from "@/components/ui/toaster";
import Navbar from "@/components/Navbar";
import SEO from "@/components/SEO";
import GoogleAnalytics from "@/components/GoogleAnalytics";
import CookieConsent from "@/components/CookieConsent";
import { ConsentProvider, useConsent } from "@/components/ConsentContext";

import logoImg from '@assets/image_1776194131058.png';

import HomePage from "./pages/home";
import LeistungenPage from "./pages/leistungen";
import ZertifikatePage from "./pages/zertifikate";
import ImpressumPage from "./pages/impressum";
import DatenschutzPage from "./pages/datenschutz";
import ServicePageWrapper from "./pages/services/ServicePageWrapper";
import { serviceData } from "./pages/services/serviceData";
import NotFound from "./pages/not-found";

const serviceRoutes = serviceData.map((s) => s.route);

export default function App() {
  return (
    <ConsentProvider>
      <AppShell />
    </ConsentProvider>
  );
}

function AppShell() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [location, setLocation] = useLocation();
  const { openSettings } = useConsent();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (window.location.pathname === "/index.html") {
      window.history.replaceState(null, "", "/");
      setLocation("/");
    }
  }, [location, setLocation]);

  const scrollToSection = (id: string) => {
    setMobileMenuOpen(false);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  useEffect(() => {
    const getPathTarget = () => {
      if (window.location.pathname === "/kontakt") return "kontakt";
      if (window.location.pathname === "/ueber-uns") return "ueber-uns";
      return "";
    };

    const scrollToHashTarget = (behavior: ScrollBehavior = "smooth") => {
      const storedTarget = sessionStorage.getItem("scrollTarget");
      const target = storedTarget || window.location.hash.replace("#", "") || getPathTarget();

      if (!target) {
        window.scrollTo({ top: 0, behavior: "auto" });
        return;
      }

      sessionStorage.removeItem("scrollTarget");
      if (window.location.pathname === "/" && window.location.hash !== `#${target}`) {
        window.history.replaceState(null, "", `/#${target}`);
      }
      window.requestAnimationFrame(() => {
        window.setTimeout(() => {
          const element = document.getElementById(target);
          if (!element) return;

          element.scrollIntoView({ behavior, block: "start" });
        }, 80);
      });
    };

    setMobileMenuOpen(false);
    scrollToHashTarget("smooth");

    const handleHashChange = () => scrollToHashTarget("smooth");
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, [location]);

  const handleSectionNav = (id: string) => {
    setMobileMenuOpen(false);
    if (location === "/" || location === "") {
      window.history.pushState(null, "", `/#${id}`);
      scrollToSection(id);
    } else {
      sessionStorage.setItem("scrollTarget", id);
      setLocation("/");
    }
  };

  return (
    <div className="min-h-screen bg-background font-sans text-foreground overflow-x-hidden">
      <SEO />
      <GoogleAnalytics />
      <Navbar
        isScrolled={isScrolled}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
      />

      <main className="pt-28 md:pt-32">
        <Switch>
          <Route path="/" component={HomePage} />
          <Route path="/index.html" component={HomePage} />
          <Route path="/ueber-uns" component={HomePage} />
          <Route path="/kontakt" component={HomePage} />
          <Route path="/leistungen" component={LeistungenPage} />
          <Route path="/zertifikate" component={ZertifikatePage} />
          <Route path="/impressum" component={ImpressumPage} />
          <Route path="/datenschutz" component={DatenschutzPage} />
          {serviceRoutes.map((route) => (
            <Route key={route} path={route} component={ServicePageWrapper} />
          ))}
          <Route component={NotFound} />
        </Switch>
      </main>

      {/* Footer */}
      <footer className="bg-zinc-50 text-zinc-600 border-t border-zinc-200">
        {/* Main Footer */}
        <div className="container mx-auto px-4 md:px-6 py-14 md:py-16">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 md:gap-8">

            {/* Spalte 1: Logo + Kurztext */}
            <div className="flex flex-col gap-4">
              <img src={logoImg} alt="Tür- und Torservice Britz" className="h-14 w-auto object-contain" />
              <p className="text-sm leading-relaxed text-zinc-500">
                Ihr Metallbau-Meisterbetrieb für Türen, Tore und Sicherheit
              </p>
            </div>

            {/* Spalte 2: Leistungen */}
            <div>
              <h3 className="text-sm font-bold text-zinc-900 uppercase tracking-wider mb-4">Leistungen</h3>
              <ul className="space-y-2 text-sm">
                {[
                  { label: "Garagentore", href: "/garagentore" },
                  { label: "Industrietore", href: "/industrietore" },
                  { label: "Brandschutztüren und -tore", href: "/brandschutztueren-tore" },
                  { label: "UVV-Prüfungen", href: "/uvv-pruefung" },
                  { label: "Metallbauarbeiten", href: "/metallbauarbeiten" },
                  { label: "Einbruchschutz", href: "/einbruchschutz" },
                  { label: "Verladebrücken", href: "/verladebruecken" },
                ].map((item) => (
                  <li key={item.href}>
                    <Link href={item.href} className="hover:text-primary transition-colors">
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Spalte 3: Kontakt */}
            <div>
              <h3 className="text-sm font-bold text-zinc-900 uppercase tracking-wider mb-4">Kontakt</h3>
              <ul className="space-y-3 text-sm">
                <li>
                  <a href="tel:0794133514" className="flex items-center gap-2 hover:text-primary transition-colors">
                    <Phone className="w-4 h-4 shrink-0 text-primary" />
                    07941 33 51 4
                  </a>
                </li>
                <li>
                  <a href="mailto:info@tts-britz.de" className="flex items-center gap-2 hover:text-primary transition-colors">
                    <Mail className="w-4 h-4 shrink-0 text-primary" />
                    info@tts-britz.de
                  </a>
                </li>
                <li>
                  <span className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 shrink-0 text-primary mt-0.5" />
                    <span>Eichklingenweg 7<br />74613 Öhringen-Eckartsweiler</span>
                  </span>
                </li>
                <li className="pt-1">
                  <button
                    onClick={() => handleSectionNav("kontakt")}
                    className="text-primary font-medium hover:underline text-sm"
                  >
                    Jetzt Termin vereinbaren →
                  </button>
                </li>
              </ul>
            </div>

            {/* Spalte 4: Rechtliches */}
            <div>
              <h3 className="text-sm font-bold text-zinc-900 uppercase tracking-wider mb-4">Rechtliches</h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link href="/impressum" className="hover:text-primary transition-colors">
                    Impressum
                  </Link>
                </li>
                <li>
                  <Link href="/datenschutz" className="hover:text-primary transition-colors">
                    Datenschutz
                  </Link>
                </li>
                <li>
                  <Link href="/zertifikate" className="hover:text-primary transition-colors">
                    Zertifikate
                  </Link>
                </li>
              </ul>
            </div>

          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-zinc-200 bg-white">
          <div className="container mx-auto px-4 md:px-6 py-4 flex flex-col sm:flex-row justify-between items-center gap-3 text-xs text-zinc-600">
            <p>&copy; {new Date().getFullYear()} Tür- und Torservice Britz. Alle Rechte vorbehalten.</p>
            <div className="flex gap-5">
              <Link href="/impressum" className="hover:text-primary transition-colors">
                Impressum
              </Link>
              <Link href="/datenschutz" className="hover:text-primary transition-colors">
                Datenschutz
              </Link>
              <button type="button" onClick={openSettings} className="hover:text-primary transition-colors">
                Datenschutzeinstellungen
              </button>
            </div>
          </div>
        </div>
      </footer>

      <CookieConsent />
      <Toaster />
    </div>
  );
}
